import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface DocViewerProps {
  content: string | null;
  isLoading: boolean;
  error: string | null;
}

export function DocViewer({ content, isLoading, error }: DocViewerProps) {
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!content) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <Typography variant="body1" color="text.secondary">
          ドキュメントを選択してください
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        overflow: 'auto',
        p: 3,
        '& .markdown-body': {
          maxWidth: '100%',
          '& h1': {
            fontSize: '2rem',
            fontWeight: 600,
            marginTop: '1.5rem',
            marginBottom: '1rem',
            borderBottom: '1px solid',
            borderColor: 'divider',
            paddingBottom: '0.5rem',
          },
          '& h2': {
            fontSize: '1.5rem',
            fontWeight: 600,
            marginTop: '1.25rem',
            marginBottom: '0.75rem',
            borderBottom: '1px solid',
            borderColor: 'divider',
            paddingBottom: '0.5rem',
          },
          '& h3': {
            fontSize: '1.25rem',
            fontWeight: 600,
            marginTop: '1rem',
            marginBottom: '0.5rem',
          },
          '& p': {
            marginBottom: '1rem',
            lineHeight: 1.7,
          },
          '& ul, & ol': {
            marginBottom: '1rem',
            paddingLeft: '2rem',
          },
          '& li': {
            marginBottom: '0.5rem',
          },
          '& code': {
            backgroundColor: 'action.hover',
            padding: '0.2rem 0.4rem',
            borderRadius: '0.25rem',
            fontSize: '0.875rem',
            fontFamily: 'monospace',
          },
          '& pre': {
            backgroundColor: 'action.hover',
            padding: '1rem',
            borderRadius: '0.5rem',
            overflow: 'auto',
            marginBottom: '1rem',
            '& code': {
              backgroundColor: 'transparent',
              padding: 0,
            },
          },
          '& blockquote': {
            borderLeft: '4px solid',
            borderColor: 'primary.main',
            paddingLeft: '1rem',
            marginLeft: 0,
            marginBottom: '1rem',
            color: 'text.secondary',
          },
          '& table': {
            width: '100%',
            borderCollapse: 'collapse',
            marginBottom: '1rem',
            '& th, & td': {
              border: '1px solid',
              borderColor: 'divider',
              padding: '0.5rem',
              textAlign: 'left',
            },
            '& th': {
              backgroundColor: 'action.hover',
              fontWeight: 600,
            },
          },
          '& a': {
            color: 'primary.main',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            },
          },
          '& img': {
            maxWidth: '100%',
            height: 'auto',
            marginBottom: '1rem',
          },
        },
      }}
    >
      <div className="markdown-body">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
      </div>
    </Box>
  );
}
